// HQ's provider status page (F-HQ-38; spec/14 §15): which external providers this deployment is wired
// to, whether each answers, and the last time HQ checked.
//
//   GET  /v1/hq/providers                    every provider, configured or not, with its status   hq:settings
//   POST /v1/hq/providers/{providerId}/test  run the provider's safe check now, record the result  hq:settings
//
// **What a provider is.** The deployment's own integrations, configured by environment (spec/20 §1.2):
// transactional email (SMTP — Resend in production, Mailpit locally), SMS and WhatsApp OTP, the game
// metadata source (IGDB through Twitch OAuth), the art source (SteamGridDB), the payment provider, object
// storage (R2 / MinIO) and the realtime bus (Valkey pub/sub). An unconfigured provider disables its
// feature rather than crashing the server, so "not configured" is a normal answer here, not an error.
//
// **Secrets never leave the server.** A provider's view names its driver and a few non-secret facts an
// operator needs to recognise it (the SMTP host and port, the bucket names, the sender's domain) — never a
// key, a password, a token or a full address. The test's error is the provider's own message with anything
// that looks like a credential stripped.
//
// **A test is safe.** It proves reachability and credentials without side effects a customer could see:
// SMTP connects and authenticates then quits (no mail is sent), IGDB and SteamGridDB run one small search,
// object storage heads a key in each bucket, the realtime bus answers a read. SMS, WhatsApp and payments
// have no side-effect-free check (a test would send a message or move money), so they report whether
// they are configured and are not testable. Every test writes one platform audit row and keeps its result
// (`hq_provider_check`), so the page shows the last check whichever api pod ran it.
import { createRoute } from '@hono/zod-openapi';
import { z } from "../zod.js";
import { commonProblemResponses, jsonResponse, problemResponse } from "../openapi/responses.js";
import { HQ_SETTINGS } from "../permissions/index.js";
import { IsoDateTimeSchema } from "../primitives/scalars.js";
import { PERMISSION_EXTENSION } from "./audit.js";
/** Every provider the page lists, in the order it lists them. */
export const HQ_PROVIDER_IDS = ['email', 'sms', 'whatsapp', 'igdb', 'steamgriddb', 'payments', 'objectStorage', 'realtime'];
export const HqProviderIdSchema = z.enum(HQ_PROVIDER_IDS).openapi('HqProviderId', {
    description: 'An external provider the deployment integrates with (F-HQ-38).',
});
/**
 * Where a provider stands:
 *
 *   not_configured  its environment is not set — the feature it serves is off (spec/20 §1.2)
 *   configured      set, and not checked yet (or not testable)
 *   reachable       the last check succeeded
 *   unreachable     the last check failed — `lastError` says why
 */
export const HQ_PROVIDER_STATUSES = ['not_configured', 'configured', 'reachable', 'unreachable'];
export const HqProviderStatusSchema = z.enum(HQ_PROVIDER_STATUSES).openapi('HqProviderStatus', {
    description: 'not_configured, configured (not checked or not testable), reachable, or unreachable (F-HQ-38).',
});
/** One non-secret fact about a provider's configuration, for the operator to recognise it. */
export const HqProviderDetailSchema = z
    .object({
    label: z.string().min(1).max(64),
    value: z.string().min(1).max(256),
})
    .openapi('HqProviderDetail', { description: 'A non-secret configuration fact (a host, a bucket, a driver). Never a credential.' });
export const HqProviderSchema = z
    .object({
    id: HqProviderIdSchema,
    /** The provider's name for people ("Email (SMTP)"). */
    name: z.string().min(1).max(64),
    /** What it is used for, one sentence. */
    purpose: z.string().min(1).max(256),
    /** The configured driver (`smtp`, `fake`, `twitch`, `s3`…), or `null` when none is. */
    driver: z.string().min(1).max(64).nullable(),
    status: HqProviderStatusSchema,
    /** Whether `POST …/test` can check it without side effects. */
    testable: z.boolean(),
    details: z.array(HqProviderDetailSchema).max(8),
    /** When HQ last tested it, or `null`. */
    lastCheckedAt: IsoDateTimeSchema.nullable(),
    /** How long the last test took, in milliseconds, or `null`. */
    lastLatencyMs: z.int().min(0).nullable(),
    /** The last test's error (credentials stripped), or `null` when it passed or never ran. */
    lastError: z.string().max(512).nullable(),
})
    .openapi('HqProvider', { description: 'An external provider: its configuration (no secrets) and the last check (F-HQ-38).' });
export const HqProviderListSchema = z
    .object({ data: z.array(HqProviderSchema).max(HQ_PROVIDER_IDS.length) })
    .openapi('HqProviderList', { description: 'Every provider the deployment knows, configured or not (F-HQ-38).' });
export const HqProviderParamsSchema = z.object({ providerId: HqProviderIdSchema }).openapi('HqProviderParams');
const forbidden = problemResponse('Not HQ, no `hq:settings`, or no second factor on the session.', 'forbidden', 'second-factor-required');
export const listHqProvidersRoute = createRoute({
    method: 'get',
    path: '/v1/hq/providers',
    operationId: 'listHqProviders',
    tags: ['hq'],
    summary: 'The deployment’s external providers and their status',
    description: 'Every provider — email, SMS, WhatsApp, IGDB, SteamGridDB, payments, object storage, realtime — with whether it is ' +
        'configured, its non-secret configuration, and the last test’s result. Reads the environment and the last checks; ' +
        'calls no provider. Platform scope: requires `hq:settings` and a second factor.',
    [PERMISSION_EXTENSION]: HQ_SETTINGS,
    responses: {
        200: jsonResponse(HqProviderListSchema, 'The providers.'),
        401: commonProblemResponses[401],
        403: forbidden,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const testHqProviderRoute = createRoute({
    method: 'post',
    path: '/v1/hq/providers/{providerId}/test',
    operationId: 'testHqProvider',
    tags: ['hq'],
    summary: 'Check one provider now',
    description: 'Runs the provider’s side-effect-free check (SMTP connect and authenticate, one IGDB or SteamGridDB search, a head in ' +
        'each bucket, a realtime bus read), records the result and returns the provider with it. A failed check is a `200` ' +
        'with `status: unreachable` and the error — the check ran. One platform audit row. `409` for a provider that is not ' +
        'configured or has no safe check. Platform scope: requires `hq:settings` and a second factor.',
    [PERMISSION_EXTENSION]: HQ_SETTINGS,
    request: { params: HqProviderParamsSchema },
    responses: {
        200: jsonResponse(HqProviderSchema, 'The check ran; the provider with its result.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        409: problemResponse('The provider is not configured, or it has no side-effect-free check.', 'conflict'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
//# sourceMappingURL=hq-providers.js.map